import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_CHAT_SESSIONS,
  API_CHAT_SESSION_ID,
  API_CHAT_SESSION_TITLE,
  API_CHAT_SESSION_PIN,
} from '../../constants/apiConstants';
import type { ContentBlock } from '../../features/chat/types/contentBlocks';

// ============================================================================
// DTOs — mirror the backend message contracts in
// SmartMatrix.Domain/Core/Chat/Messages/ChatSession_Messages.cs
// ============================================================================

/** Compact session header for list rows and the active-session pointer. */
export interface ChatSessionSummary {
  id: number;
  title: string;
  lastActiveAt: string;
  createdAt: string;
  messageCount: number;
  pinned: boolean;
  totalTokens: number;
}

/** One persisted message in a session detail response. */
export interface ChatSessionMessage {
  id: number;
  role: 'user' | 'assistant' | 'system';
  blocks: ContentBlock[];
  /** Raw JSON string from the backend — frontend parses lazily if needed. */
  tokenUsageJson?: string | null;
  correlationId?: string | null;
  createdAt: string;
}

/** Full session detail returned by GET /sessions/{id}. */
export interface ChatSessionDetail {
  session: ChatSessionSummary;
  messages: ChatSessionMessage[];
}

export interface ChatSessionListResult {
  items: ChatSessionSummary[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface ChatSessionListParams {
  pageNumber?: number;
  pageSize?: number;
  /** Optional tenant scope. Omit to list every session the user owns. */
  tenantId?: number | null;
}

// ============================================================================
// Service
// ============================================================================

const chatSessionService = {
  async listSessions(params?: ChatSessionListParams): Promise<ChatSessionListResult> {
    try {
      const response = await apiClient.get(API_CHAT_SESSIONS, {
        params: {
          pageNumber: params?.pageNumber,
          pageSize: params?.pageSize,
          tenantId: params?.tenantId ?? undefined,
        },
      });
      // Backend wraps the response in Result<T> with `succeeded` + `data`.
      // Be tolerant: some services return the bare payload, others wrap it.
      const payload = response.data?.data ?? response.data;
      return {
        items: payload?.items ?? [],
        pageNumber: payload?.pageNumber ?? 1,
        pageSize: payload?.pageSize ?? 20,
        totalCount: payload?.totalCount ?? 0,
        totalPages: payload?.totalPages ?? 0,
      };
    } catch (error) {
      logger.error('chatSessionService.listSessions', getErrorMessage(error));
      throw error;
    }
  },

  async getSession(id: number, tenantId?: number | null): Promise<ChatSessionDetail | null> {
    try {
      const response = await apiClient.get(API_CHAT_SESSION_ID(id), {
        params: { tenantId: tenantId ?? undefined },
      });
      const payload = response.data?.data ?? response.data;
      if (!payload?.session) return null;
      return {
        session: payload.session,
        messages: payload.messages ?? [],
      };
    } catch (error) {
      logger.error('chatSessionService.getSession', getErrorMessage(error));
      throw error;
    }
  },

  async renameSession(id: number, title: string, tenantId?: number | null): Promise<ChatSessionSummary | null> {
    try {
      const response = await apiClient.patch(
        API_CHAT_SESSION_TITLE(id),
        { title },
        { params: { tenantId: tenantId ?? undefined } }
      );
      const payload = response.data?.data ?? response.data;
      return payload?.session ?? null;
    } catch (error) {
      logger.error('chatSessionService.renameSession', getErrorMessage(error));
      throw error;
    }
  },

  async pinSession(id: number, pinned: boolean, tenantId?: number | null): Promise<ChatSessionSummary | null> {
    try {
      const response = await apiClient.patch(
        API_CHAT_SESSION_PIN(id),
        { pinned },
        { params: { tenantId: tenantId ?? undefined } }
      );
      const payload = response.data?.data ?? response.data;
      return payload?.session ?? null;
    } catch (error) {
      logger.error('chatSessionService.pinSession', getErrorMessage(error));
      throw error;
    }
  },

  async deleteSession(id: number, tenantId?: number | null): Promise<void> {
    try {
      await apiClient.delete(API_CHAT_SESSION_ID(id), {
        params: { tenantId: tenantId ?? undefined },
      });
    } catch (error) {
      logger.error('chatSessionService.deleteSession', getErrorMessage(error));
      throw error;
    }
  },
};

export default chatSessionService;
